import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { SignupService } from 'src/app/services/signup/signup.service';
import { MaskPipe } from 'ngx-mask';
import { ActivatedRoute, Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-powers',
  templateUrl: './dashboard-approval-powers.component.html',
  styleUrls: ['./dashboard-approval-powers.component.scss'],
})
export class DashboardApprovalPowersComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() comparingVersions: boolean = false;

  membersArr: any[] = [];
  loading = true;

  // Dados para versão principal
  mainData: Groups[] = [];
  mainPowerGroupData: RelevancePowerValues[] = [];
  mainRepresentatives: any[] = [];
  mainLoading = true;

  // Dados para versão comparando
  comparingData: Groups[] = [];
  comparingPowerGroupData: RelevancePowerValues[] = [];
  comparingRepresentatives: any[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  powerGroupDetails = false;
  selectedPowerGroup: Partial<RelevancePowerValues> = null;
  selectedPowerGroupVersionType: 'main' | 'comparing' | null = null;

  data: Groups[] = [];
  powerGroupData: RelevancePowerValues[] = [];
  representatives: any[] = [];
  canView = false;
  role: string = '';

  eventMainVersion: any;
  eventComparingVersion: any;
  eventVersion: any;

  constructor(
    private signupService: SignupService,
    private maskPipe: MaskPipe,
    private router: Router,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private el: ElementRef,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    this.canView = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_powers_details`
    );

    if (this.comparingVersions) {
      // Se já tem versões selecionadas, carregar dados
      if (this.approvalService.approvalData.mainVersion) {
        this.loadMainVersion();
      }
      if (this.approvalService.approvalData.comparingVersion) {
        this.loadComparingVersion();
      }
    } else {
      if (this.approvalService.approvalData.powers) {
        this.data =
          this.approvalService.approvalData.powers.register.signature_group;
        this.powerGroupData =
          this.approvalService.approvalData.powers.register.signature_power;
        this.representatives =
          this.approvalService.approvalData.powers.register.representatives;
        this.loading = false;
      } else {
        // Buscar dados da API v3 se não houver no cache
        this.getApprovalData();
      }
    }

    // Listener para mudanças de versão
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();

        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange
    ) {
      const previousValue = changes['comparingVersions'].previousValue;
      const currentValue = changes['comparingVersions'].currentValue;

      // Se mudou de true para false, recarregar dados
      if (previousValue === true && currentValue === false) {
        if (this.approvalService.approvalData.powers) {
          this.data =
            this.approvalService.approvalData.powers.register.signature_group;
          this.powerGroupData =
            this.approvalService.approvalData.powers.register.signature_power;
          this.representatives =
            this.approvalService.approvalData.powers.register.representatives;
          this.loading = false;
        } else {
          this.getApprovalData();
        }
      }
    }
  }

  ngOnDestroy() {
    this.eventVersion?.unsubscribe();
    this.eventMainVersion?.unsubscribe();
    this.eventComparingVersion?.unsubscribe();
  }

  // Função para comparar os dados de Grupos e aplicar classes de comparação
  async compareData() {
    if (!this.mainData || !this.comparingData) {
      return;
    }

    try {
      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita) - Grupos
      this.comparingData.forEach((comparingItem) => {
        const referenceItem = this.mainData.find(
          (ref) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const mainMembers = JSON.stringify(
            referenceItem.members?.sort() || []
          );
          const comparingMembers = JSON.stringify(
            comparingItem.members?.sort() || []
          );

          const changedFields = {
            name: comparingItem.name !== referenceItem.name,
            representative_type:
              comparingItem.representative_type !==
              referenceItem.representative_type,
            members: mainMembers !== comparingMembers,
          };

          const hasChanges = Object.values(changedFields).some(
            (changed) => changed
          );

          // Adicionar campos formatados para exibição
          const formattedItem = {
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          };

          processedData.push(formattedItem);
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem) => {
        const existsInComparing = this.comparingData.find(
          (comp) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingData = processedData;

      // Processar Grupos de Poder
      await this.comparePowerGroupData();

      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    }
  }

  // Função para comparar os dados de Grupos de Poder e aplicar classes de comparação
  async comparePowerGroupData() {
    if (!this.mainPowerGroupData || !this.comparingPowerGroupData) {
      return;
    }

    try {
      // Helper para normalizar valores em arrays ordenados (evita erro de sort em número/null)
      const normalizeToSortedArray = (value: any): any[] => {
        if (Array.isArray(value)) {
          return [...value].sort();
        }
        if (value === null || value === undefined) {
          return [];
        }
        // Se vier número ou string, transformamos em array com 1 elemento
        return [value];
      };

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita) - Grupos de Poder
      this.comparingPowerGroupData.forEach((comparingItem) => {
        const referenceItem = this.mainPowerGroupData.find(
          (ref) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const mainPowerType = JSON.stringify(
            normalizeToSortedArray(referenceItem.power_type)
          );
          const comparingPowerType = JSON.stringify(
            normalizeToSortedArray(comparingItem.power_type)
          );
          const mainSecondaryGroup = JSON.stringify(
            normalizeToSortedArray(referenceItem.secondary_group)
          );
          const comparingSecondaryGroup = JSON.stringify(
            normalizeToSortedArray(comparingItem.secondary_group)
          );
          const mainRequiredSigns = JSON.stringify(
            normalizeToSortedArray(referenceItem.required_signs)
          );
          const comparingRequiredSigns = JSON.stringify(
            normalizeToSortedArray(comparingItem.required_signs)
          );
          const mainNecessarySigns = JSON.stringify(
            normalizeToSortedArray(referenceItem.necessary_signs)
          );
          const comparingNecessarySigns = JSON.stringify(
            normalizeToSortedArray(comparingItem.necessary_signs)
          );
          const mainMainGroup = JSON.stringify(
            normalizeToSortedArray(referenceItem.main_group)
          );
          const comparingMainGroup = JSON.stringify(
            normalizeToSortedArray(comparingItem.main_group)
          );

          const changedFields = {
            power_type: mainPowerType !== comparingPowerType,
            main_group: mainMainGroup !== comparingMainGroup,
            secondary_group: mainSecondaryGroup !== comparingSecondaryGroup,
            required_signs_quantity:
              referenceItem.required_signs_quantity !==
              comparingItem.required_signs_quantity,
            necessary_signs_quantity:
              referenceItem.necessary_signs_quantity !==
              comparingItem.necessary_signs_quantity,
            required_signs: mainRequiredSigns !== comparingRequiredSigns,
            necessary_signs: mainNecessarySigns !== comparingNecessarySigns,
            max_sign_amount:
              referenceItem.max_sign_amount !== comparingItem.max_sign_amount,
            min_sign_amount:
              referenceItem.min_sign_amount !== comparingItem.min_sign_amount,
            description:
              referenceItem.description !== comparingItem.description,
          };

          const hasChanges = Object.values(changedFields).some(
            (changed) => changed
          );

          // Adicionar campos formatados para exibição
          const formattedItem = {
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          };

          processedData.push(formattedItem);
        } else {
          // Item novo (existe só na direita)

          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainPowerGroupData.forEach((referenceItem) => {
        const existsInComparing = this.comparingPowerGroupData.find(
          (comp) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed

          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingPowerGroupData = processedData;
    } catch (error) {
      console.error(
        '[DashboardApprovalPowers] Erro ao comparar grupos de poder',
        error
      );
    }
  }

  loadComparingVersions() {
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = [];
      this.mainPowerGroupData = [];
      this.mainRepresentatives = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = [];
      this.comparingPowerGroupData = [];
      this.comparingRepresentatives = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing').then(() => {
      // Após carregar os dados, fazer a comparação
      this.compareData();
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    });
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      // Obter register_uuid da rota
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainPowerGroupData = [];
          this.mainRepresentatives = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingPowerGroupData = [];
          this.comparingRepresentatives = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
        } else {
          this.data = [];
          this.powerGroupData = [];
          this.representatives = [];
          this.loading = false;
        }
        this.cdr.detectChanges();
        return;
      }

      // Parâmetros da API v3
      // Se houver versão selecionada no approvalService, usar ela; caso contrário, usar last=true
      // Fazer três chamadas: signature_groups, signature_power e representatives
      const paramsGroups: any = {
        data_type: 'signature_groups',
      };

      const paramsPowers: any = {
        data_type: 'signature_power',
      };

      const paramsRepresentatives: any = {
        data_type: 'representatives',
      };

      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        paramsGroups.version = versionToUse;
        paramsPowers.version = versionToUse;
        paramsRepresentatives.version = versionToUse;
      } else {
        paramsGroups.last = true;
        paramsPowers.last = true;
        paramsRepresentatives.last = true;
      }

      // Chamar APIs v3 em paralelo
      const [
        signatureGroupsResponse,
        signaturePowersResponse,
        representativesResponse,
      ] = await Promise.all([
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: paramsGroups,
          token: true,
        }),
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: paramsPowers,
          token: true,
        }),
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: paramsRepresentatives,
          token: true,
        }),
      ]);

      // Processar representantes primeiro para poder mapear os membros dos grupos
      let representatives: any[] = [];
      if (representativesResponse && representativesResponse.data) {
        const representativesArray =
          representativesResponse?.data?.versions?.representatives
            ?.versions?.[0]?.data;
        if (representativesArray && Array.isArray(representativesArray)) {
          representatives = representativesArray.map((rep: any) => {
            // O ID pode estar em 'id', 'user' ou pode ser o próprio objeto
            const repId =
              rep.id || rep.user || (typeof rep === 'number' ? rep : null);
            return {
              ...rep,
              id: repId,
              // Garantir que temos o ID numérico para comparação
              userId: rep.user || repId,
              full_name: rep.full_name || rep.name || '',
              document_number:
                rep.document_number || rep.document || rep.cpf_cnpj || '',
              cpf_cnpj:
                rep.document_number || rep.document || rep.cpf_cnpj || '',
            };
          });
        }
      }

      // Processar resposta da API v3
      // Combinar as duas respostas para processar
      const combinedResponse = {
        data: {
          ...signatureGroupsResponse?.data,
          versions: {
            ...signatureGroupsResponse?.data?.versions,
            signature_power:
              signaturePowersResponse?.data?.versions?.signature_power,
          },
        },
      };

      if (
        (signatureGroupsResponse && signatureGroupsResponse.data) ||
        (signaturePowersResponse && signaturePowersResponse.data)
      ) {
        const processedData = this.processSignatureGroupsData(
          combinedResponse,
          representatives
        );

        if (versionType === 'main') {
          this.mainData = processedData.signatureGroups || [];
          this.mainPowerGroupData = processedData.signaturePowers || [];
          this.mainRepresentatives = processedData.representatives || [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = processedData.signatureGroups || [];
          this.comparingPowerGroupData = processedData.signaturePowers || [];
          this.comparingRepresentatives = processedData.representatives || [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          // Se já temos main data, fazer comparação
          if (this.comparingVersions && this.mainData) {
            await this.compareData();
          }
          this.cdr.detectChanges();
        } else {
          this.data = processedData.signatureGroups || [];
          this.powerGroupData = processedData.signaturePowers || [];
          this.representatives = processedData.representatives || [];
          this.loading = false;

          // Armazenar no cache apenas se não estiver em modo de comparação
          if (!this.comparingVersions && processedData.signatureGroups) {
            // Criar estrutura similar à v2 para compatibilidade
            this.approvalService.approvalData.powers = {
              register: {
                signature_group: processedData.signatureGroups,
                signature_power: processedData.signaturePowers,
                representatives: processedData.representatives,
              },
            } as any;
          }

          this.cdr.detectChanges();
        }
      } else {
        // Sem dados na resposta
        if (versionType === 'main') {
          this.mainData = [];
          this.mainPowerGroupData = [];
          this.mainRepresentatives = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingPowerGroupData = [];
          this.comparingRepresentatives = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
        } else {
          this.data = [];
          this.powerGroupData = [];
          this.representatives = [];
          this.loading = false;
        }
        this.cdr.detectChanges();
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainData = [];
        this.mainPowerGroupData = [];
        this.mainRepresentatives = [];
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingData = [];
        this.comparingPowerGroupData = [];
        this.comparingRepresentatives = [];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
      } else {
        this.data = [];
        this.powerGroupData = [];
        this.representatives = [];
        this.loading = false;
      }
      this.cdr.detectChanges();
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição normal (não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }

    /* Código antigo comentado - não chamar APIs v2
    try {
      if (versionType === 'main' || versionType === 'comparing') {
        const req_params = {
          register: this.route.snapshot.params.id,
          tab: 'powers',
        };

        if (
          versionType === 'main' &&
          this.approvalService.approvalData.mainVersion
        ) {
          req_params['version'] = this.approvalService.approvalData.mainVersion;
        } else if (
          versionType === 'comparing' &&
          this.approvalService.approvalData.comparingVersion
        ) {
          req_params['version'] =
            this.approvalService.approvalData.comparingVersion;
        }

        const data = await this.api.get<any[]>({
          route: `api/v2/approvals/version/`,
          token: true,
          params: req_params,
        });

        if (versionType === 'main') {
          this.mainData = data[0].register.signature_group;
          this.mainPowerGroupData = data[0].register.signature_power;
          this.mainRepresentatives = data[0].register.representatives;
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = data[0].register.signature_group;
          this.comparingPowerGroupData = data[0].register.signature_power;
          this.comparingRepresentatives = data[0].register.representatives;
          this.comparingLoading = false;
        }
        return;
      }

      const req_params = {
        register: this.route.snapshot.params.id,
        tab: 'powers',
      };

      if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      if (versionType === 'main') {
        this.mainData = data[0].register.signature_group;
        this.mainPowerGroupData = data[0].register.signature_power;
        this.mainRepresentatives = data[0].register.representatives;
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingData = data[0].register.signature_group;
        this.comparingPowerGroupData = data[0].register.signature_power;
        this.comparingRepresentatives = data[0].register.representatives;
        this.comparingLoading = false;
      } else {
        this.approvalService.approvalData.powers = data[0];
        this.data = data[0].register.signature_group;
        this.powerGroupData = data[0].register.signature_power;
        this.representatives = data[0].register.representatives;
        this.loading = false;
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
      } else {
        this.loading = false;
      }
    }
    */
  }

  formatPower(power: any, isObj: boolean = false) {
    let powersArr: any[] = [];
    let authorizations = this.signupService.signatureAuthorizationChoices;

    const newPower = !isObj ? power : power.map((item) => item.id);

    if (!newPower) return '-';

    if (newPower.length > 0) {
      authorizations.map((item) => {
        if (newPower.includes(Number(item.value))) {
          powersArr.push(item.label);
        }
      });
    }

    return powersArr ? powersArr.join(', ') : '-';
  }

  formatDefaultPower(power: any) {
    // let powersArr: any[] = [];
    // let authorizations = this.signupService.signatureAuthorizationChoices;
    // if (power.length > 0) {
    //   authorizations.map((item) => {
    //     if (power.power_type[0].id.includes(Number(item.value))) {
    //       powersArr.push(item.label);
    //     }
    //   });
    // }
    // return powersArr ? powersArr.join(', ') : '';
  }

  formatGroup(
    groupIds: any,
    main_group: boolean = true,
    versionType: 'main' | 'comparing' | null = null
  ) {
    const dataToUse = this.getDataForView(versionType);
    return this.formatGroupStatic(groupIds, main_group, dataToUse);
  }

  /**
   * Retorna o nome do grupo principal para uma linha de poder.
   * Prioriza:
   * 1) main_group_data.name, se existir
   * 2) Busca pelo id em signature_group (data/mainData/comparingData)
   */
  getMainGroupName(
    power: RelevancePowerValues | any,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    // 1) Se veio do backend já com main_group_data, usar diretamente
    const mainGroupData = (power as any).main_group_data;
    if (mainGroupData && mainGroupData.name) {
      return mainGroupData.name;
    }

    // 2) Caso contrário, tentar resolver pelo id usando os dados de grupos
    const dataToUse = this.getDataForView(versionType);

    let groupId: number | null = null;

    // Novos dados (v3) normalmente vêm como número
    if (typeof (power as any).main_group === 'number') {
      groupId = (power as any).main_group;
    } else if (
      Array.isArray((power as any).main_group) &&
      (power as any).main_group.length > 0
    ) {
      // Compatibilidade com possíveis formatos antigos (array)
      const first = (power as any).main_group[0];
      groupId =
        typeof first === 'object' && first !== null ? first.id : Number(first);
    }

    if (
      groupId === null ||
      groupId === undefined ||
      !dataToUse ||
      dataToUse.length === 0
    ) {
      return '-';
    }

    const found = dataToUse.find(
      (group) => Number(group.id) === Number(groupId)
    );
    return found?.name || '-';
  }

  formatGroupStatic(
    groupIds: any,
    main_group: boolean = true,
    data: Groups[]
  ): string {
    let groupsArr: any[] = [];

    if (!groupIds) return '-';

    if (data.length > 0) {
      if (main_group) {
        const finded = data.find((item) => item.id === groupIds[0]?.id);
        return finded?.name || '-';
      }

      if (!groupIds.id) return '-';

      data.forEach((item) => {
        groupIds.id.forEach((i) => {
          if (i === item.id) {
            groupsArr.push(item.name);
          }
        });
      });
    }

    return groupsArr.length > 0 ? groupsArr.join(', ') : '-';
  }

  formatPowerIntegrants(
    membersId: number[],
    versionType: 'main' | 'comparing' | null = null
  ) {
    let usersArr: any[] = [];
    const representativesToUse = this.getRepresentativesForView(versionType);

    if (!membersId) return '-';

    if (representativesToUse.length > 0) {
      representativesToUse.map((item) => {
        if (membersId.includes(item.id)) {
          usersArr.push(
            `${item.full_name} | ${this.formatCpf(item.document_number)}`
          );
        }
      });
    }

    return usersArr ? usersArr.sort().join('\r\n') : '';
  }

  formatGroupIntegrants(
    membersId: number[],
    versionType: 'main' | 'comparing' | null = null
  ) {
    const representativesToUse = this.getRepresentativesForView(versionType);
    return this.formatGroupIntegrantsStatic(membersId, representativesToUse);
  }

  formatGroupIntegrantsStatic(
    membersId: number[],
    representatives: any[]
  ): string {
    let usersArr: any[] = [];

    if (!membersId) return '-';

    if (representatives.length > 0) {
      representatives.forEach((item) => {
        if (membersId.includes(item.id)) {
          usersArr.push(
            `${item.full_name} | ${this.formatCpf(item.document_number)}`
          );
        }
      });
    }

    return usersArr.length > 0 ? usersArr.sort().join(', ') : '';
  }

  // Métodos auxiliares para obter dados baseados no tipo de versão
  getDataForView(versionType: 'main' | 'comparing' | null = null): Groups[] {
    if (!this.comparingVersions) return this.data;
    if (versionType === 'main') return this.mainData;
    if (versionType === 'comparing') {
      // Incluir grupos removidos (que existem apenas no main) também no lado direito
      const removedGroups = this.mainData.filter(
        (mainGroup) =>
          !this.comparingData.find(
            (comparingGroup) => comparingGroup.id === mainGroup.id
          )
      );
      return [...this.comparingData, ...removedGroups];
    }
    return this.data;
  }

  getPowerGroupDataForView(
    versionType: 'main' | 'comparing' | null = null
  ): RelevancePowerValues[] {
    if (!this.comparingVersions) return this.powerGroupData;
    if (versionType === 'main') return this.mainPowerGroupData;
    if (versionType === 'comparing') {
      // Incluir linhas removidas (que existem apenas no main) também no lado direito
      const removedRows = this.mainPowerGroupData.filter(
        (mainRow) =>
          !this.comparingPowerGroupData.find(
            (comparingRow) => comparingRow.id === mainRow.id
          )
      );
      return [...this.comparingPowerGroupData, ...removedRows];
    }
    return this.powerGroupData;
  }

  // Verifica se um grupo da versão main foi removido na versão comparing
  isGroupRemovedInComparing(group: Groups): boolean {
    if (!this.comparingVersions) return false;
    // Se comparingData é null, undefined ou array vazio, todos os grupos do main foram removidos
    if (!this.comparingData || this.comparingData.length === 0) return true;
    return !this.comparingData.find(
      (comparingGroup) => comparingGroup.id === group.id
    );
  }

  // Verifica se um poder da versão main foi removido na versão comparing
  isPowerRemovedInComparing(power: RelevancePowerValues): boolean {
    if (!this.comparingVersions) return false;
    // Se comparingPowerGroupData é null, undefined ou array vazio, todos os poderes do main foram removidos
    if (!this.comparingPowerGroupData || this.comparingPowerGroupData.length === 0) return true;
    return !this.comparingPowerGroupData.find(
      (comparingPower) => comparingPower.id === power.id
    );
  }

  /**
   * Processa os dados de grupos e poderes da API v3
   * Estrutura da resposta:
   * - data.versions.signature_groups.versions[0].data (array de grupos)
   * - data.versions.signature_power.versions[0].data (array de poderes)
   * @param representatives Array de representantes para mapear os IDs dos membros
   */
  private processSignatureGroupsData(
    response: any,
    representatives: any[] = []
  ): {
    signatureGroups: Groups[];
    signaturePowers: RelevancePowerValues[];
    representatives: any[];
  } {
    // Extrair grupos de assinatura
    const signatureGroupsArray =
      response?.data?.versions?.signature_groups?.versions?.[0]?.data;

    // Extrair poderes de assinatura
    const signaturePowersArray =
      response?.data?.versions?.signature_power?.versions?.[0]?.data;

    // Processar grupos - mapear os IDs dos membros para os dados completos dos representantes
    const signatureGroups: Groups[] =
      signatureGroupsArray && Array.isArray(signatureGroupsArray)
        ? signatureGroupsArray.map((group: any) => {
            // Mapear IDs dos membros para dados completos dos representantes
            // Os IDs em group.members podem ser números (ex: [2025])
            const membersData =
              group.members &&
              Array.isArray(group.members) &&
              representatives.length > 0
                ? group.members
                    .map((memberId: number) => {
                      // Tentar encontrar o representante por diferentes campos de ID
                      const representative = representatives.find(
                        (rep: any) =>
                          rep.id === memberId ||
                          rep.user === memberId ||
                          rep.userId === memberId ||
                          Number(rep.id) === Number(memberId) ||
                          Number(rep.user) === Number(memberId)
                      );
                      if (representative) {
                      } else {
                      }
                      return representative || null;
                    })
                    .filter((rep: any) => rep !== null)
                : [];

            return {
              ...group,
              // Garantir campos esperados
              name: group.name || '',
              representative_type: group.representative_type || null,
              representative_type_display:
                group.representative_type_display ||
                group.representative_type ||
                '',
              members: group.members || [], // Manter IDs originais para compatibilidade
              // Adicionar dados completos dos membros se disponível
              membersData: membersData,
            } as Groups;
          })
        : [];

    // Processar poderes
    const signaturePowers: RelevancePowerValues[] =
      signaturePowersArray && Array.isArray(signaturePowersArray)
        ? signaturePowersArray.map(
            (power: any) =>
              ({
                ...power,
                // Garantir campos esperados
                power_type: power.power_type || [],
                main_group: power.main_group || null,
                secondary_group: power.secondary_group || [],
                required_signs_quantity: power.required_signs_quantity || 0,
                required_signs: power.required_signs || [],
                necessary_signs_quantity: power.necessary_signs_quantity || 0,
                necessary_signs: power.necessary_signs || [],
                min_sign_amount: power.min_sign_amount || null,
                max_sign_amount: power.max_sign_amount || null,
                description: power.description || '',
                indefinite_signature_value:
                  power.indefinite_signature_value || false,
              } as RelevancePowerValues)
          )
        : [];

    return {
      signatureGroups,
      signaturePowers,
      representatives,
    };
  }

  getRepresentativesForView(
    versionType: 'main' | 'comparing' | null = null
  ): any[] {
    if (!this.comparingVersions) return this.representatives;
    if (versionType === 'main') return this.mainRepresentatives;
    if (versionType === 'comparing') return this.comparingRepresentatives;
    return this.representatives;
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null): boolean {
    if (!this.comparingVersions) return this.loading;
    if (versionType === 'main') return this.mainLoading;
    if (versionType === 'comparing') return this.comparingLoading;
    return this.loading;
  }

  closeModal() {
    this.powerGroupDetails = false;
    this.selectedPowerGroup = null;
    this.selectedPowerGroupVersionType = null;
  }

  openModal(
    item: Partial<RelevancePowerValues>,
    versionType: 'main' | 'comparing' | null = null
  ) {
    this.powerGroupDetails = true;
    this.selectedPowerGroup = item;
    this.selectedPowerGroupVersionType = versionType;
  }

  openModalById(id: string) {
    try {
      let allData: any[] = [];

      if (this.comparingVersions) {
        allData = [...this.mainPowerGroupData, ...this.comparingPowerGroupData];
      } else {
        allData = this.powerGroupData;
      }

      const finded = allData.find((item: any) => {
        if (item.id) {
          return String(item.id) === String(id);
        }
        return false;
      });

      if (finded) {
        // Determinar o tipo de versão
        let versionType: 'main' | 'comparing' | null = null;
        if (this.comparingVersions) {
          const isInMain = this.mainPowerGroupData.some(
            (item: any) => String(item.id) === String(id)
          );
          const isInComparing = this.comparingPowerGroupData.some(
            (item: any) => String(item.id) === String(id)
          );
          if (isInMain) {
            versionType = 'main';
          } else if (isInComparing) {
            versionType = 'comparing';
          }
        }
        this.openModal(finded, versionType);
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  formatCpf(value: string) {
    const newValue = this.maskPipe.transform(value, '000.000.000-00');

    return newValue;
  }

  formatMaxValue(item) {
    if (item.indefinite_signature_value) return 'Valor Indefinido';

    if (item.max_sign_amount) {
      return Utils.formatBrl(Number(item.max_sign_amount));
    } else {
      return '-';
    }
  }

  renderLabel() {
    const assignor = this.router.url.includes('assignor');

    return assignor
      ? 'Representantes e Avalistas/Dev. Solidários'
      : 'Representantes';
  }

  //   setHistoryData() {
  //     this.data =
  //       this.approval.history.SignatureGroup ||
  //       this.approval.register.signature_group;
  //     this.powerGroupData =
  //       this.approval.history.SignaturePower ||
  //       this.approval.register.signature_power;
  //     this.representatives =
  //       this.approval.history.Representative ||
  //       this.approval.register.representatives;
  //   }
}
